import { useCallback, useEffect, useState } from "react";

import { AlertCircle, Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useEnsureProfile } from "~/hooks/useEnsureProfile";
import { usePaymentProofs } from "~/hooks/usePaymentProofs";
import { PaymentProofForm } from "~/pages/clients/components/client-detail/payment-proof/PaymentProofForm";
import { useProfileStore } from "~/store/profileStore";
import { PaymentProof, UpdatePaymentProofData } from "~/types/payment";

interface PaymentProofDialogProps {
  clientId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  paymentProof?: PaymentProof;
  onSuccess?: () => void;
}

export const PaymentProofDialog = ({
  clientId,
  isOpen,
  onOpenChange,
  paymentProof,
  onSuccess,
}: PaymentProofDialogProps) => {
  const isEditing = !!paymentProof;
  const { addPaymentProof, editPaymentProof, isUploading, isSubmitting } =
    usePaymentProofs(clientId);
  const { id: userId } = useProfileStore();
  const {
    isProfileLoaded,
    isLoading: isProfileLoading,
    error: profileError,
    refreshProfile,
  } = useEnsureProfile();
  const [userError, setUserError] = useState<string | null>(null);
  const [shouldAttemptRefresh, setShouldAttemptRefresh] = useState(false);

  // Function to actively check for user ID
  const checkForUserId = useCallback(async () => {
    if (!userId) {
      console.log("No user ID in dialog, trying to refresh profile");
      setShouldAttemptRefresh(true);

      // Try the direct force sync check first
      const forcedId = await useProfileStore.getState().forceSyncProfileCheck();

      if (forcedId) {
        console.log("User ID found via force sync:", forcedId);
        setUserError(null);
        setShouldAttemptRefresh(false);
        return;
      }

      // If that fails, try the regular refresh
      await refreshProfile();

      // Check if we have an ID now
      const currentId = useProfileStore.getState().id;
      if (!currentId) {
        console.error("Still no user ID after multiple refresh attempts");
        setUserError(
          "No se pudo identificar el usuario. Por favor recarga la página o inicia sesión nuevamente."
        );
      } else {
        console.log("User ID found after refresh:", currentId);
        setUserError(null);
      }

      setShouldAttemptRefresh(false);
    } else {
      console.log("User ID is present:", userId);
      setUserError(null);
    }
  }, [userId, refreshProfile]);

  // Check if user ID is available when dialog opens
  useEffect(() => {
    if (isOpen) {
      console.log("Dialog opened, checking for user ID. Current state:", {
        userId,
        isProfileLoaded,
        isProfileLoading,
      });

      // Only attempt refresh if we're not already doing so
      if (!isProfileLoading && !shouldAttemptRefresh) {
        checkForUserId();
      }
    }
  }, [
    isOpen,
    userId,
    isProfileLoaded,
    isProfileLoading,
    checkForUserId,
    shouldAttemptRefresh,
  ]);

  // Set error from profile loading
  useEffect(() => {
    if (profileError) {
      setUserError(`Error al cargar el perfil: ${profileError}`);
    }
  }, [profileError]);

  const handleSubmit = async (
    data: {
      rut: string;
      amount: number;
      salesRequestId?: string;
      comments?: string;
    },
    file?: File
  ) => {
    // Get the most up-to-date user ID
    const currentUserId = useProfileStore.getState().id;

    // Prevent submission if user ID is not available
    if (!currentUserId) {
      setUserError(
        "No se pudo identificar el usuario. Por favor recarga la página o inicia sesión nuevamente."
      );
      return;
    }

    let success = false;

    if (isEditing && paymentProof) {
      const updateData: UpdatePaymentProofData = {
        rut: data.rut,
        amount: data.amount,
        salesRequestId: data.salesRequestId,
        comments: data.comments,
      };
      const result = await editPaymentProof(paymentProof.id, updateData, file);
      success = !!result;
    } else {
      const result = await addPaymentProof(
        {
          rut: data.rut,
          amount: data.amount,
          salesRequestId: data.salesRequestId,
          comments: data.comments,
        },
        file
      );
      success = !!result;
    }

    if (success) {
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const isFormDisabled =
    !!userError || isProfileLoading || shouldAttemptRefresh;

  // Handle manual refresh
  const handleManualRefresh = async () => {
    setUserError(null);
    setShouldAttemptRefresh(true);

    // Try force sync first
    const forcedId = await useProfileStore.getState().forceSyncProfileCheck();

    if (!forcedId) {
      // If force sync fails, try regular refresh
      await refreshProfile();
    }

    setShouldAttemptRefresh(false);
    checkForUserId();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Only allow closing dialog if not in the middle of submitting
        if (!isSubmitting && !isUploading) {
          onOpenChange(open);
        }
      }}
    >
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {isEditing
              ? "Editar Comprobante de Pago"
              : "Crear Nuevo Comprobante de Pago"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Modifique los detalles del comprobante de pago existente."
              : "Registre un nuevo comprobante de pago y vincularlo automáticamente con transacciones bancarias."}
          </DialogDescription>
        </DialogHeader>

        {(isProfileLoading || shouldAttemptRefresh) && (
          <Alert className="border-blue-200 bg-blue-50">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <AlertDescription className="text-blue-700">
              Cargando información del usuario...
            </AlertDescription>
          </Alert>
        )}

        {userError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {userError}
              <button
                onClick={handleManualRefresh}
                className="ml-2 text-red-700 underline hover:text-red-900"
                disabled={isProfileLoading || shouldAttemptRefresh}
              >
                Reintentar
              </button>
            </AlertDescription>
          </Alert>
        )}

        <PaymentProofForm
          clientId={clientId}
          initialData={paymentProof}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          isUploading={isUploading}
          isSubmitting={isSubmitting}
          disabled={isFormDisabled}
        />
      </DialogContent>
    </Dialog>
  );
};
